import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, MapPin, Clock, Users } from 'lucide-react';
import sanityClient from '../lib/sanityClient';
import SEO from '../components/SEO';

function GameDetailsPage() {
  const { id } = useParams();
  const [game, setGame] = useState(null);
  const [playerStats, setPlayerStats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) {
      setError('No game ID provided');
      setIsLoading(false);
      return;
    }

    // Fetch game details and player stats
    sanityClient
      .fetch(
        `*[_type == "event" && _id == $id][0]{
          _id,
          title,
          startDate,
          location,
          opponent,
          isHomeGame,
          isComplete,
          ourScore,
          opponentScore,
          ourShots,
          opponentShots,
          periods[] {
            periodNumber,
            ourScore,
            opponentScore
          },
          playerStats[] {
            "player": player->{
              _id,
              name,
              number,
              position,
              positionGroup,
              slug,
              captainStatus,
              "imageUrl": image.asset->url
            },
            goals,
            assists,
            penaltyMinutes,
            powerPlayGoals,
            powerPlayAssists,
            shortHandedGoals,
            overtimeGoals,
            emptyNetGoals,
            shotsAgainst,
            goalsAgainst,
            saves,
            minutesPlayed
          }
        }`,
        { id }
      )
      .then((data) => {
        if (!data) {
          setError('Game not found');
        } else {
          setGame(data);

          // Sort player stats by position group then points
          if (data.playerStats) {
            const sortedStats = [...data.playerStats].sort((a, b) => {
              // First sort by position group
              const posGroupOrder = { Forward: 0, Defense: 1, Goalie: 2 };
              const posGroupA = posGroupOrder[a.player.positionGroup] || 0;
              const posGroupB = posGroupOrder[b.player.positionGroup] || 0;

              if (posGroupA !== posGroupB) {
                return posGroupA - posGroupB;
              }

              // Then by points (goals + assists) descending
              const pointsA = (a.goals || 0) + (a.assists || 0);
              const pointsB = (b.goals || 0) + (b.assists || 0);

              return pointsB - pointsA;
            });

            setPlayerStats(sortedStats);
          }
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching game details:', err);
        setError('Failed to load game details. Please try again later.');
        setIsLoading(false);
      });
  }, [id]);

  // Generate dynamic SEO data based on game information
  const generateGameSEO = () => {
    if (!game) {
      return {
        title: 'Game Details - Bombardiers Hockey',
        description:
          'View game details, scores, and player statistics for the Bombardiers Hockey team.',
        image: '/bomb-puck-logo.png',
      };
    }
    // Format the date
    const gameDate = game.startDate
      ? new Date(game.startDate).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })
      : '';

    // Create matchup string
    const matchup = game.isHomeGame
      ? `Bombardiers vs ${game.opponent}`
      : `${game.opponent} vs Bombardiers`;

    // Create title based on game status
    let title;
    let description;

    if (game.isComplete && game.ourScore !== undefined && game.opponentScore !== undefined) {
      // Completed game with score
      const result =
        game.ourScore > game.opponentScore
          ? 'Win'
          : game.ourScore < game.opponentScore
            ? 'Loss'
            : 'Tie';

      title = `${matchup} ${game.ourScore}-${game.opponentScore} (${result}) - ${gameDate} | Bombardiers Hockey`;

      description = `${result} against ${game.opponent} with a final score of ${game.ourScore}-${game.opponentScore}. View complete game stats, player performance, and highlights from ${gameDate}.`;

      // Add season context if available
      if (game.seasonName) {
        description += ` ${game.seasonName} season game.`;
      }

      // Add top performers if available
      if (playerStats.length > 0) {
        const topScorer = playerStats
          .filter((p) => p.player.positionGroup !== 'Goalie')
          .find((p) => (p.goals || 0) + (p.assists || 0) > 0);

        if (topScorer) {
          const points = (topScorer.goals || 0) + (topScorer.assists || 0);
          description += ` ${topScorer.player.name} led with ${points} point${points !== 1 ? 's' : ''}.`;
        }
      }
    } else {
      // Upcoming or incomplete game
      title = `${matchup} - ${gameDate} | Bombardiers Hockey`;

      if (game.isComplete) {
        description = `Game details for ${matchup} on ${gameDate}. View player statistics and game information.`;
      } else {
        const gameTime = game.startDate
          ? new Date(game.startDate).toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            })
          : '';

        description = `Upcoming ${game.isHomeGame ? 'home' : 'away'} game against ${game.opponent} on ${gameDate}`;
        if (gameTime) description += ` at ${gameTime}`;
        if (game.location) description += ` at ${game.location}`;
        description += '. Check back after the game for complete stats and results.';
      }

      if (game.seasonName) {
        description += ` Part of the ${game.seasonName} season.`;
      }
    }

    return {
      title,
      description,
      image: '/bomb-puck-logo.png', // Could be enhanced with game photos
      type: 'article',
    };
  };

  if (isLoading) {
    return (
      <div className="bg-black min-h-screen flex justify-center items-center">
        <div className="text-white text-xl">Loading game details...</div>
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="bg-black min-h-screen flex justify-center items-center">
        <div className="bg-gray-900 p-6 rounded-lg text-white text-xl">
          {error || 'Game not found'}
        </div>
      </div>
    );
  }

  // Format date
  const formatGameDate = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Format time
  const formatGameTime = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Determine game result
  const getGameResult = () => {
    if (!game.isComplete) {
      return 'Upcoming';
    }

    if (game.ourScore > game.opponentScore) {
      return 'Win';
    } else if (game.ourScore < game.opponentScore) {
      return 'Loss';
    } else {
      return 'Tie';
    }
  };

  const gameResult = getGameResult();
  const resultColorClass =
    gameResult === 'Win'
      ? 'bg-green-700'
      : gameResult === 'Loss'
        ? 'bg-orange-700'
        : gameResult === 'Tie'
          ? 'bg-yellow-700'
          : 'bg-blue-700';

  const gameSEO = generateGameSEO();

  // Mobile player card component
  const PlayerStatCard = ({ statLine, isGoalie = false }) => {
    const points = (statLine.goals || 0) + (statLine.assists || 0);
    const totalShots = (statLine.saves || 0) + (statLine.goalsAgainst || 0);
    const savePercentage =
      totalShots > 0 ? (((statLine.saves || 0) / totalShots) * 100).toFixed(1) : '0.0';

    return (
      <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
        <Link to={`/players/${statLine.player.slug.current}`} className="block">
          <div className="flex items-center mb-3">
            <div className="h-12 w-12 rounded-full overflow-hidden bg-gray-700 mr-3 flex items-center justify-center">
              <img
                src={statLine.player.imageUrl || '/player-placeholder.png'}
                alt={statLine.player.name}
                className="w-full h-full object-contain bg-gray-800"
                onError={(e) => {
                  e.currentTarget.src = '/player-placeholder.png';
                  e.currentTarget.classList.remove('object-cover');
                  e.currentTarget.classList.add('object-contain', 'p-1');
                }}
              />
            </div>

            <div className="flex-1">
              <div className="text-white font-medium flex items-center">
                #{statLine.player.number} {statLine.player.name}
                {statLine.player.captainStatus === 'captain' && (
                  <span className="ml-1 text-yellow-500 font-bold">(C)</span>
                )}
                {statLine.player.captainStatus === 'associate' && (
                  <span className="ml-1 text-gray-400 font-bold">(A)</span>
                )}
              </div>
              <div className="text-gray-400 text-sm">{statLine.player.position}</div>
            </div>

            <div className="text-right">
              {!isGoalie ? (
                <>
                  <div className="text-yellow-500 text-xl font-bold">{points}</div>
                  <div className="text-gray-400 text-sm">PTS</div>
                </>
              ) : (
                <>
                  <div className="text-yellow-500 text-xl font-bold">{savePercentage}%</div>
                  <div className="text-gray-400 text-sm">SV%</div>
                </>
              )}
            </div>
          </div>

          {!isGoalie ? (
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-white font-medium">{statLine.goals || 0}</div>
                <div className="text-gray-400 text-xs">G</div>
              </div>
              <div>
                <div className="text-white font-medium">{statLine.assists || 0}</div>
                <div className="text-gray-400 text-xs">A</div>
              </div>
              <div>
                <div className="text-white font-medium">{statLine.penaltyMinutes || 0}</div>
                <div className="text-gray-400 text-xs">PIM</div>
              </div>
              <div>
                <div className="text-white font-medium">{statLine.minutesPlayed || 0}</div>
                <div className="text-gray-400 text-xs">MIN</div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-white font-medium">{statLine.shotsAgainst || 0}</div>
                <div className="text-gray-400 text-xs">SA</div>
              </div>
              <div>
                <div className="text-white font-medium">{statLine.goalsAgainst || 0}</div>
                <div className="text-gray-400 text-xs">GA</div>
              </div>
              <div>
                <div className="text-white font-medium">{statLine.saves || 0}</div>
                <div className="text-gray-400 text-xs">SV</div>
              </div>
              <div>
                <div className="text-white font-medium">{statLine.minutesPlayed || 0}</div>
                <div className="text-gray-400 text-xs">MIN</div>
              </div>
            </div>
          )}

          {/* Secondary stats for skaters */}
          {!isGoalie && (
            <div className="grid grid-cols-3 gap-4 text-center mt-3 pt-3 border-t border-gray-700">
              <div>
                <div className="text-white text-sm">{statLine.powerPlayGoals || 0}</div>
                <div className="text-gray-400 text-xs">PPG</div>
              </div>
              <div>
                <div className="text-white text-sm">{statLine.shortHandedGoals || 0}</div>
                <div className="text-gray-400 text-xs">SHG</div>
              </div>
              <div>
                <div className="text-white text-sm">{statLine.overtimeGoals || 0}</div>
                <div className="text-gray-400 text-xs">OTG</div>
              </div>
            </div>
          )}
        </Link>
      </div>
    );
  };

  return (
    <>
      <SEO {...gameSEO} />
      <div className="bg-black min-h-screen py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Game Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-4">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 lg:mb-0">
                {game.isHomeGame
                  ? 'Bombardiers vs ' + game.opponent
                  : game.opponent + ' vs Bombardiers'}
              </h1>

              {game.isComplete && (
                <div
                  className={`${resultColorClass} text-white px-4 py-2 rounded-md font-bold text-lg`}
                >
                  {gameResult} {game.ourScore}-{game.opponentScore}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-gray-400">
              <div className="flex items-center">
                <Calendar className="h-5 w-5 mr-2" />
                <span className="text-sm">{formatGameDate(game.startDate)}</span>
              </div>
              <div className="flex items-center">
                <Clock className="h-5 w-5 mr-2" />
                <span className="text-sm">{formatGameTime(game.startDate)}</span>
              </div>
              <div className="flex items-center">
                <MapPin className="h-5 w-5 mr-2" />
                <span className="text-sm">{game.location || 'Location TBD'}</span>
              </div>
              {game.isComplete && (
                <div className="flex items-center">
                  <Users className="h-5 w-5 mr-2" />
                  <span className="text-sm">
                    Shots: {game.ourShots || 0} - {game.opponentShots || 0}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Game is not complete message */}
          {!game.isComplete && (
            <div className="bg-gray-900 rounded-lg p-8 text-center mb-8">
              <div className="text-xl text-white mb-4">This game has not been played yet.</div>
              <div className="text-gray-400">
                Game details will appear here once the game is complete.
              </div>
            </div>
          )}

          {/* Game Results Section */}
          {game.isComplete && (
            <>
              {/* Score by Period */}
              {game.periods && game.periods.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-white mb-4">Score by Period</h2>

                  {/* Mobile: Card Layout */}
                  <div className="lg:hidden">
                    <div className="bg-gray-900 rounded-lg p-4">
                      <div className="space-y-4">
                        {/* Headers */}
                        <div className="grid grid-cols-4 gap-2 text-center font-medium text-gray-400 text-sm">
                          <div>Team</div>
                          {game.periods.slice(0, 3).map((period) => (
                            <div key={period.periodNumber}>P{period.periodNumber}</div>
                          ))}
                        </div>

                        {/* Bombardiers Row */}
                        <div className="grid grid-cols-4 gap-2 text-center">
                          <div className="text-yellow-500 font-medium text-sm">BOMB</div>
                          {game.periods.slice(0, 3).map((period) => (
                            <div key={period.periodNumber} className="text-white font-bold">
                              {period.ourScore}
                            </div>
                          ))}
                        </div>

                        {/* Opponent Row */}
                        <div className="grid grid-cols-4 gap-2 text-center">
                          <div className="text-white font-medium text-sm">
                            {game.opponent.substring(0, 4).toUpperCase()}
                          </div>
                          {game.periods.slice(0, 3).map((period) => (
                            <div key={period.periodNumber} className="text-white font-bold">
                              {period.opponentScore}
                            </div>
                          ))}
                        </div>

                        {/* Final Score */}
                        <div className="border-t border-gray-700 pt-3">
                          <div className="grid grid-cols-2 gap-4 text-center">
                            <div>
                              <div className="text-yellow-500 font-medium text-sm">BOMBARDIERS</div>
                              <div className="text-2xl font-bold text-white">{game.ourScore}</div>
                            </div>
                            <div>
                              <div className="text-white font-medium text-sm">
                                {game.opponent.toUpperCase()}
                              </div>
                              <div className="text-2xl font-bold text-white">
                                {game.opponentScore}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Overtime periods if any */}
                        {game.periods.length > 3 && (
                          <div className="border-t border-gray-700 pt-3">
                            <div className="text-center text-gray-400 text-sm mb-2">Overtime</div>
                            {game.periods.slice(3).map((period) => (
                              <div
                                key={period.periodNumber}
                                className="grid grid-cols-3 gap-2 text-center text-sm mb-2"
                              >
                                <div className="text-gray-400">OT{period.periodNumber - 3}</div>
                                <div className="text-yellow-500">{period.ourScore}</div>
                                <div className="text-white">{period.opponentScore}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Desktop: Table Layout */}
                  <div className="hidden lg:block bg-gray-900 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-800">
                      <thead className="bg-gray-800">
                        <tr>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                          >
                            Team
                          </th>
                          {game.periods.map((period) => (
                            <th
                              key={period.periodNumber}
                              scope="col"
                              className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider"
                            >
                              Period {period.periodNumber}
                            </th>
                          ))}
                          <th
                            scope="col"
                            className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider"
                          >
                            Final
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-gray-900 divide-y divide-gray-800">
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap font-medium text-yellow-500">
                            Bombardiers
                          </td>
                          {game.periods.map((period) => (
                            <td
                              key={period.periodNumber}
                              className="px-6 py-4 whitespace-nowrap text-center text-white"
                            >
                              {period.ourScore}
                            </td>
                          ))}
                          <td className="px-6 py-4 whitespace-nowrap text-center font-bold text-white">
                            {game.ourScore}
                          </td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap font-medium text-white">
                            {game.opponent}
                          </td>
                          {game.periods.map((period) => (
                            <td
                              key={period.periodNumber}
                              className="px-6 py-4 whitespace-nowrap text-center text-white"
                            >
                              {period.opponentScore}
                            </td>
                          ))}
                          <td className="px-6 py-4 whitespace-nowrap text-center font-bold text-white">
                            {game.opponentScore}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Player Stats */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-white mb-4">Player Statistics</h2>

                {playerStats.length > 0 ? (
                  <>
                    {/* Mobile: Card Layout */}
                    <div className="lg:hidden">
                      {/* Skaters */}
                      <div className="mb-8">
                        <h3 className="text-lg font-semibold text-yellow-500 mb-4">Skaters</h3>
                        <div className="space-y-4">
                          {playerStats
                            .filter((stat) => stat.player.positionGroup !== 'Goalie')
                            .map((statLine) => (
                              <PlayerStatCard
                                key={statLine.player._id}
                                statLine={statLine}
                                isGoalie={false}
                              />
                            ))}
                        </div>
                      </div>

                      {/* Goalies */}
                      {playerStats.filter((stat) => stat.player.positionGroup === 'Goalie').length >
                        0 && (
                        <div className="mb-8">
                          <h3 className="text-lg font-semibold text-yellow-500 mb-4">Goalies</h3>
                          <div className="space-y-4">
                            {playerStats
                              .filter((stat) => stat.player.positionGroup === 'Goalie')
                              .map((statLine) => (
                                <PlayerStatCard
                                  key={statLine.player._id}
                                  statLine={statLine}
                                  isGoalie={true}
                                />
                              ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Desktop: Table Layout */}
                    <div className="hidden lg:block">
                      {/* Skaters Table */}
                      <div className="bg-gray-900 rounded-lg overflow-hidden mb-8">
                        <table className="min-w-full divide-y divide-gray-800">
                          <thead className="bg-gray-800">
                            <tr>
                              <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                              >
                                Player
                              </th>
                              <th
                                scope="col"
                                className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider"
                              >
                                G
                              </th>
                              <th
                                scope="col"
                                className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider"
                              >
                                A
                              </th>
                              <th
                                scope="col"
                                className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider"
                              >
                                PTS
                              </th>
                              <th
                                scope="col"
                                className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider"
                              >
                                PIM
                              </th>
                              <th
                                scope="col"
                                className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider"
                              >
                                PPG
                              </th>
                              <th
                                scope="col"
                                className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider"
                              >
                                PPA
                              </th>
                              <th
                                scope="col"
                                className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider"
                              >
                                SHG
                              </th>
                              <th
                                scope="col"
                                className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider"
                              >
                                OTG
                              </th>
                              <th
                                scope="col"
                                className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider"
                              >
                                ENG
                              </th>
                              <th
                                scope="col"
                                className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider"
                              >
                                MIN
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-gray-900 divide-y divide-gray-800">
                            {playerStats
                              .filter((stat) => stat.player.positionGroup !== 'Goalie')
                              .map((statLine) => (
                                <tr key={statLine.player._id} className="hover:bg-gray-800">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <Link
                                      to={`/players/${statLine.player.slug.current}`}
                                      className="flex items-center group"
                                    >
                                      <div className="h-8 w-8 rounded-full overflow-hidden bg-gray-700 mr-3 flex items-center justify-center">
                                        <img
                                          src={
                                            statLine.player.imageUrl || '/player-placeholder.png'
                                          }
                                          alt={statLine.player.name}
                                          className="w-full h-full object-contain bg-gray-800"
                                          onError={(e) => {
                                            e.currentTarget.src = '/player-placeholder.png';
                                            e.currentTarget.classList.remove('object-cover');
                                            e.currentTarget.classList.add('object-contain', 'p-1');
                                          }}
                                        />
                                      </div>
                                      <div>
                                        <div className="text-white group-hover:text-yellow-400 flex items-center">
                                          {statLine.player.name}
                                          {statLine.player.captainStatus === 'captain' && (
                                            <span className="ml-1 text-yellow-500 font-bold">
                                              (C)
                                            </span>
                                          )}
                                          {statLine.player.captainStatus === 'associate' && (
                                            <span className="ml-1 text-gray-400 font-bold">
                                              (A)
                                            </span>
                                          )}
                                        </div>
                                        <div className="text-gray-400 text-sm">
                                          #{statLine.player.number} | {statLine.player.position}
                                        </div>
                                      </div>
                                    </Link>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-white">
                                    {statLine.goals || 0}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-white">
                                    {statLine.assists || 0}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center font-bold text-white">
                                    {(statLine.goals || 0) + (statLine.assists || 0)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-white">
                                    {statLine.penaltyMinutes || 0}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-white">
                                    {statLine.powerPlayGoals || 0}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-white">
                                    {statLine.powerPlayAssists || 0}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-white">
                                    {statLine.shortHandedGoals || 0}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-white">
                                    {statLine.overtimeGoals || 0}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-white">
                                    {statLine.emptyNetGoals || 0}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-white">
                                    {statLine.minutesPlayed || 0}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Goalie Stats if applicable */}
                      {playerStats.filter((stat) => stat.player.positionGroup === 'Goalie').length >
                        0 && (
                        <div className="mb-8">
                          <h2 className="text-2xl font-bold text-white mb-4">Goalie Statistics</h2>
                          <div className="bg-gray-900 rounded-lg overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-800">
                              <thead className="bg-gray-800">
                                <tr>
                                  <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                                  >
                                    Goalie
                                  </th>
                                  <th
                                    scope="col"
                                    className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider"
                                  >
                                    SA
                                  </th>
                                  <th
                                    scope="col"
                                    className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider"
                                  >
                                    GA
                                  </th>
                                  <th
                                    scope="col"
                                    className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider"
                                  >
                                    SV
                                  </th>
                                  <th
                                    scope="col"
                                    className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider"
                                  >
                                    SV%
                                  </th>
                                  <th
                                    scope="col"
                                    className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider"
                                  >
                                    MIN
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-gray-900 divide-y divide-gray-800">
                                {playerStats
                                  .filter((stat) => stat.player.positionGroup === 'Goalie')
                                  .map((statLine) => {
                                    const totalShots =
                                      (statLine.saves || 0) + (statLine.goalsAgainst || 0);
                                    const savePercentage =
                                      totalShots > 0
                                        ? (((statLine.saves || 0) / totalShots) * 100).toFixed(1)
                                        : '0.0';

                                    return (
                                      <tr key={statLine.player._id} className="hover:bg-gray-800">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                          <Link
                                            to={`/players/${statLine.player.slug.current}`}
                                            className="flex items-center group"
                                          >
                                            <div className="h-8 w-8 rounded-full overflow-hidden bg-gray-700 mr-3 flex items-center justify-center">
                                              <img
                                                src={
                                                  statLine.player.imageUrl ||
                                                  '/player-placeholder.png'
                                                }
                                                alt={statLine.player.name}
                                                className="w-full h-full object-contain bg-gray-800"
                                                onError={(e) => {
                                                  e.currentTarget.src = '/player-placeholder.png';
                                                  e.currentTarget.classList.remove('object-cover');
                                                  e.currentTarget.classList.add(
                                                    'object-contain',
                                                    'p-1'
                                                  );
                                                }}
                                              />
                                            </div>
                                            <div>
                                              <div className="text-white group-hover:text-yellow-400 flex items-center">
                                                {statLine.player.name}
                                                {statLine.player.captainStatus === 'captain' && (
                                                  <span className="ml-1 text-yellow-500 font-bold">
                                                    (C)
                                                  </span>
                                                )}
                                                {statLine.player.captainStatus === 'associate' && (
                                                  <span className="ml-1 text-gray-400 font-bold">
                                                    (A)
                                                  </span>
                                                )}
                                              </div>
                                              <div className="text-gray-400 text-sm">
                                                #{statLine.player.number}
                                              </div>
                                            </div>
                                          </Link>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center text-white">
                                          {statLine.shotsAgainst || 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center text-white">
                                          {statLine.goalsAgainst || 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center text-white">
                                          {statLine.saves || 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center text-white">
                                          {savePercentage}%
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center text-white">
                                          {statLine.minutesPlayed || 0}
                                        </td>
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="bg-gray-900 p-8 rounded-lg text-center">
                    <p className="text-gray-400 text-lg">
                      No player statistics available for this game.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default GameDetailsPage;
